import React, { useEffect } from "react";
import { CountryDropdown } from "react-country-region-selector";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore";

/**
 * Countries the active campaign will accept (DEV-3732).
 *
 * Comes from the product pack response rather than the page template, because
 * the campaign follows the pack and the pack follows the affiliate tag - a
 * template-level lookup would describe a different campaign than the one the
 * order bills through.
 *
 * Empty means "unknown" - a CRM outage, a cache miss, or a pack with no
 * campaign - and CountryDropdown treats an empty whitelist as no filtering at
 * all, so the shopper still gets every country. Konnektive still declines a
 * restricted one and the checkout shows the translated message, so failing
 * open here costs feedback quality rather than enforcement.
 */
const allowedCountriesFor = (allowedCountries, isSubscribe) => {
  const list = isSubscribe ? allowedCountries?.subscription : allowedCountries?.base;

  return Array.isArray(list) ? list : [];
};

const CountryField = ({ label, value, error, name, handleChange }) => {
  const { country_code, allowed_countries } = useProductData();
  const isSubscribe = useCartData((state) => state.isSubscribe);
  const isEnhancedContrast = window.enhancedContrast;

  // Subscriptions bill through their own campaign, which can allow a different
  // set of countries, so the list has to follow the toggle.
  const allowedCountries = allowedCountriesFor(allowed_countries, isSubscribe);

  const resolvedValue = value?.toUpperCase() || country_code;
  const isFiltering = allowedCountries.length > 0;
  const isResolvedAllowed = !isFiltering || allowedCountries.includes(resolvedValue);

  // A value the filtered list does not contain matches no <option>, so the
  // select renders blank while form state still holds it - and the order goes
  // out with a country the campaign refuses. Happens when a campaign's own
  // default country is not on its allowed list, or when email prefill writes
  // straight to form state. Clear it so the shopper has to pick, and so the
  // required-field validation fires instead of the CRM declining later.
  useEffect(() => {
    if (isFiltering && !isResolvedAllowed) {
      handleChange("");
    }
  }, [isFiltering, isResolvedAllowed]);

  if (country_code) {
    return (
      <div className="input-container" style={{ width: "100%" }}>
        <div
          className={`input ${error && "input--error"} ${isEnhancedContrast && "enhancedContrast"} ${isEnhancedContrast && "enhancedContrast" && error && "enhancedContrast__error"}`}
        >
          {label && (
            <span
              className={`styledLabel styledLabel--active ${isEnhancedContrast && "enhancedContrast" && "styledLabel__enhancedContrast"}`}
            >
              {label}
            </span>
          )}
          <div className="input--customSelect">
            <CountryDropdown
              // CountryDropdown filters its options once, in the constructor -
              // it has no componentDidUpdate and no getDerivedStateFromProps,
              // so a whitelist that changes later is silently ignored. Keying
              // on the list forces a remount when the campaign changes.
              key={allowedCountries.join(",")}
              id="country"
              valueType="short"
              labelType="full"
              value={isResolvedAllowed ? resolvedValue : ""}
              whitelist={allowedCountries}
              classes={`input--select overflowed-select ${isEnhancedContrast && "enhancedContrast"}`}
              autoComplete="country-name"
              name={name}
              onChange={(value) => {
                handleChange(value);
              }}
            />
          </div>
        </div>
        {error && (
          <div className="formError" style={{ marginTop: "4px" }}>
            {error}
          </div>
        )}
      </div>
    );
  }
};

export default CountryField;
